/**
 * Content for the three pillar lead-generation guides (X/Twitter, LinkedIn and
 * Reddit) plus the hub page that links them.
 *
 * These are the site's main organic-search entry points, so the content lives
 * in data rather than JSX: it keeps both pages structurally identical (same
 * heading order, same schema shape) while letting each one say platform-
 * specific things, and it makes the JSON-LD derivable instead of hand-written.
 */

export const GUIDE_PUBLISHED = "2026-08-18";
export const GUIDE_UPDATED = "2026-08-18";

const TWITTER = {
  key: "twitter",
  slug: "twitter-lead-generation",
  path: "/twitter-lead-generation",
  platform: "X (Twitter)",
  shortName: "X",
  navLabel: "Find leads on X (Twitter)",
  seo: {
    title: "How to Find Leads on X (Twitter): 9 Proven Methods",
    description:
      "Find B2B leads on X (Twitter): search operators that surface buying intent, competitor complaint mining, and a reply-first sequence you can run in 30 minutes a day.",
    keywords:
      "how to find leads on twitter, twitter lead generation, X lead generation, find customers on twitter, twitter advanced search operators, twitter prospecting, social selling on X, twitter b2b leads, buying intent on twitter, twitter lead generation tool",
  },
  hero: {
    eyebrow: "Playbook · X (Twitter)",
    h1: "How to find leads on X (Twitter)",
    h1Italic: "without cold DMs.",
    intro:
      "X is the only major network where almost every post is public, searchable, and timestamped to the minute. That makes it the fastest place to catch someone in the ten minutes they are actively shopping for what you sell. This is the full playbook: the search operators, the signals worth chasing, the outreach sequence, and the daily routine that keeps it running.",
  },
  // Answer-first block: written to stand alone as a featured-snippet / AI-answer
  // candidate, so it repeats the question before answering it.
  answer: {
    heading: "The short answer",
    body: "To find leads on X (Twitter), search for buying intent rather than for your product category. Run advanced-search queries such as (\"anyone recommend\" OR \"looking for a\") \"project management tool\" -filter:links lang:en, switch to the Latest tab, and reply publicly within the first hour while the post is still live. Layer competitor-complaint monitoring and ICP Lists on top of that, score what you find, and move to DMs only after the person has replied to you in the open.",
    bullets: [
      "Search intent phrases, not brand keywords — \"anyone recommend\" beats \"CRM\".",
      "Always read the Latest tab; Top hides the posts that are still answerable.",
      "Reply in public first. The thread is seen by the author and everyone watching.",
      "DM only after a public exchange, and never with a pitch as the opener.",
      "Score every lead 0–100 on intent and ICP fit so follow-up goes to the right 5%.",
    ],
  },
  quickFacts: [
    {
      title: "Public by default",
      body: "Almost every post is searchable without a connection or a shared group — unlike LinkedIn, you do not need a relationship to see the conversation.",
    },
    {
      title: "The window is one hour",
      body: "Replies land while a post is still circulating. After a day the author has usually chosen a vendor and moved on.",
    },
    {
      title: "The tooling is free",
      body: "Advanced search costs nothing. X Pro (the old TweetDeck) and longer posts sit behind Premium, but every query below runs on a free account.",
    },
    {
      title: "Replies beat DMs",
      body: "A public answer earns credibility with the author and with every other person reading the thread. A cold DM only reaches one inbox.",
    },
  ],
  signalsIntro:
    "Not every mention is a lead. These are the eight post patterns worth acting on, roughly in order of how quickly they convert.",
  signals: [
    {
      signal: "Direct request for a recommendation",
      example: "\"Anyone know a good scheduling tool for a 3-person agency?\"",
      why: "Highest intent there is. The person has stated the need, the constraint, and the timing in one post.",
    },
    {
      signal: "Frustration with an incumbent",
      example: "\"Third outage this month. I'm done.\"",
      why: "Active dissatisfaction with a paid product means budget already exists — you are replacing a line item, not creating one.",
    },
    {
      signal: "Switching or cancelling",
      example: "\"Cancelling our seats at renewal, looking at alternatives.\"",
      why: "A renewal date is a deadline. These convert on a predictable clock.",
    },
    {
      signal: "Build-versus-buy debate",
      example: "\"Do we build this internally or just pay for something?\"",
      why: "The buyer is pricing options in public. A concrete answer with numbers usually ends the debate in your favour.",
    },
    {
      signal: "New role or job change",
      example: "\"Thrilled to be joining Acme as Head of RevOps.\"",
      why: "New leaders re-evaluate the stack in their first 90 days and often arrive with a mandate to change something.",
    },
    {
      signal: "Funding or launch announcement",
      example: "\"We closed our seed round.\"",
      why: "New capital turns into new tooling and new headcount within a quarter.",
    },
    {
      signal: "Hiring for an adjacent role",
      example: "\"Hiring a data engineer to build our reporting pipeline.\"",
      why: "A job description is a public admission of a gap. Sometimes your product is cheaper than the hire.",
    },
    {
      signal: "A how-to question you can answer",
      example: "\"How is everyone handling GDPR deletion requests?\"",
      why: "Lower intent, but answering well is how you get seen by everyone in the thread — including the three lurkers with the same problem.",
    },
  ],
  cheatsheet: {
    heading: "X advanced search operators for lead generation",
    intro:
      "Paste these into the X search bar, then click the Latest tab. Swap the quoted phrases for your own category and competitor names. Parentheses group alternatives, quotes match exact phrases, and a leading minus excludes.",
    rows: [
      {
        query: `("anyone recommend" OR "any recommendations for") ("crm" OR "sales tool") -filter:links lang:en`,
        finds:
          "Plain-language requests for a recommendation, with link-spam and non-English noise stripped out.",
      },
      {
        query: `("looking for a" OR "need a good") "email tool" min_faves:1 -filter:retweets`,
        finds:
          "Real accounts with at least one like — a cheap filter against bots — and no duplicate retweets.",
      },
      {
        query: `"alternative to" competitorname -filter:replies since:2026-08-01`,
        finds:
          "Fresh displacement intent. Excluding replies keeps you on original posts rather than sub-thread chatter.",
      },
      {
        query: `("switching from" OR "moving off" OR "fed up with") competitorname`,
        finds:
          "Complaint mining. These are the highest-converting posts on the platform for most B2B tools.",
      },
      {
        query: `to:competitorname ("still waiting" OR "broken" OR "no response")`,
        finds:
          "Unhappy customers complaining directly at a competitor's support handle, in public.",
      },
      {
        query: `list:1234567890 ("recommend" OR "looking for" OR "anyone using")`,
        finds:
          "Intent inside a curated List of your ICP — the single highest signal-to-noise query you can run.",
      },
      {
        query: `url:yourdomain.com -from:yourhandle`,
        finds:
          "People linking to you without tagging you. Warm, and almost always unanswered.",
      },
      {
        query: `("just raised" OR "we're hiring" OR "excited to announce") "series a"`,
        finds:
          "Trigger events: funding, expansion and launches that free up budget.",
      },
      {
        query: `min_replies:5 "how do you all handle"`,
        finds:
          "Busy discussion threads worth joining — one good answer reaches everyone subscribed to the thread.",
      },
      {
        query: `filter:verified ("need a" OR "shopping for") "agency"`,
        finds:
          "Narrows to paying accounts, which skews toward businesses rather than throwaways.",
      },
    ],
    notes: [
      "since: and until: take YYYY-MM-DD and are the cleanest way to keep results fresh.",
      "min_faves:, min_retweets: and min_replies: are the best bot filters available.",
      "Location operators (near:, within:) have been unreliable for years — filter geography from the profile instead.",
      "Save each working query as a browser bookmark or an X Pro column. A query you have to retype is a query you stop running.",
    ],
  },
  methodsIntro:
    "Nine methods, ordered from the fastest to set up to the most compounding. Most teams get results from the first three alone; the rest are what turn a good week into a repeatable channel.",
  methods: [
    {
      id: "intent-search",
      title: "Search buying intent, not your category",
      summary:
        "The mistake almost everyone makes is searching for their product name or category. Those results are full of competitors, job posts and news. Buyers do not write \"CRM\" — they write \"anyone know something that does X without costing a fortune\".",
      steps: [
        "List the five ways a customer describes the problem before they know your category exists.",
        "Wrap each in an intent phrase: \"anyone recommend\", \"looking for a\", \"need a\", \"what do you use for\", \"is there a tool that\".",
        "Add -filter:links to drop promotional posts and lang:en to cut noise.",
        "Run it on the Latest tab and scan the last 24 hours only.",
        "Bookmark the query. Re-run it every morning.",
      ],
      example: `("what do you use for" OR "is there a tool that") "client onboarding" -filter:links lang:en`,
      tip: "If a query returns more than about 30 posts a day it is too broad — add a constraint rather than skimming.",
    },
    {
      id: "competitor-mining",
      title: "Mine competitor complaints and mentions",
      summary:
        "Every competitor has a public queue of unhappy customers: people @-ing their support handle, venting about pricing changes, or announcing they are leaving. This is the shortest path from search to revenue because the budget already exists.",
      steps: [
        "Build a list of every competitor's handle, product name and common misspellings.",
        "Run to:competitor queries for support complaints and plain-name queries for general venting.",
        "Set up a standing query for \"alternative to <competitor>\" and check it daily.",
        "Reply with something useful about the actual problem — never with \"we do that better\".",
        "Log the account even when they are not ready. Renewal dates come around.",
      ],
      example: `("alternative to" OR "switching from" OR "cancelling") competitorname -filter:retweets`,
      tip: "Do not pile on a competitor's outage. Answer the underlying question; the reader can do the comparison themselves.",
    },
    {
      id: "lists",
      title: "Build ICP Lists and monitor them as columns",
      summary:
        "A List of 200 people who match your ICP turns a noisy platform into a private feed. Everything they post is relevant by construction, so intent queries scoped to the List have almost no false positives.",
      steps: [
        "Create a private List per segment — persona, industry, company size, or the customers of one competitor.",
        "Seed it from your CRM, from conference speaker pages, and from the follower lists of niche accounts.",
        "Add anyone who replies to your posts or engages with a competitor.",
        "Search inside it with list:<list-id> plus your intent phrases.",
        "In X Pro, pin one column per List and one per saved search.",
      ],
      example: `list:1234567890 ("looking for" OR "recommend" OR "frustrated with")`,
      tip: "Keep Lists private. A public List of your prospects tells your competitors exactly who to call.",
    },
    {
      id: "engagement-mining",
      title: "Work the replies under high-signal posts",
      summary:
        "When a well-known account in your space posts \"what tool do you use for X\", the replies are a pre-qualified list of buyers, complainers and evaluators — often dozens of them in one thread.",
      steps: [
        "Identify the ten accounts your buyers actually read: creators, newsletters, competitors, community leads.",
        "Turn on notifications for them so you see the post early.",
        "When they post something adjacent to your category, read every reply.",
        "Reply to the individuals with a real problem, not to the original poster.",
        "Follow everyone worth a second look and add them to the right List.",
      ],
      example: `min_replies:10 ("what do you use for" OR "recommend me") "analytics"`,
      tip: "The best leads in a thread are the people arguing about tradeoffs, not the people naming a tool.",
    },
    {
      id: "trigger-events",
      title: "Track trigger events, not just complaints",
      summary:
        "Funding, launches, hiring and job changes all precede spending. Someone who just took a new role has a change mandate and a budget; someone who just raised is about to buy a stack.",
      steps: [
        "Run standing searches for \"excited to announce\", \"thrilled to be joining\", \"we just raised\", \"we're hiring\" plus your qualifiers.",
        "Combine with a job title so you catch the buyer rather than the whole company.",
        "Congratulate first, with no ask. Come back with substance a week later.",
        "Record the date — the 90-day window after a job change is the whole opportunity.",
      ],
      example: `("excited to be joining" OR "thrilled to announce I") ("head of" OR "vp of") marketing`,
      tip: "Trigger events are worth reaching out to even at low intent, because timing is doing most of the work.",
    },
    {
      id: "communities",
      title: "Show up in Communities and Spaces",
      summary:
        "X Communities are topic-scoped feeds where the moderation keeps the noise out, and Spaces put you in a room with people who chose to spend an hour on your topic. Both are underused by B2B teams.",
      steps: [
        "Join the three or four Communities where your buyers already are; read for a week before posting.",
        "Answer questions inside the Community rather than posting links out of it.",
        "Attend Spaces on your topic and request the mic with a specific, useful point.",
        "After a Space, follow up with the people who spoke — that shared context makes a DM welcome.",
      ],
      example: "No query needed — browse Communities by topic and check Spaces daily in your niche.",
      tip: "One good Space appearance is usually worth a month of cold outreach, and it stays discoverable afterwards.",
    },
    {
      id: "profile",
      title: "Turn your profile into a landing page",
      summary:
        "Every good reply sends a handful of people to your profile. If it does not immediately say who you help and what to do next, the reply was wasted attention.",
      steps: [
        "Write a bio in the form: who you help + the outcome + the proof.",
        "Pin a post that demonstrates the product on a real problem, not a launch announcement.",
        "Put a direct link in the profile — to a demo or a specific page, not the homepage.",
        "Keep your last five posts useful, because they are the sample everyone judges you on.",
      ],
      example: "Bio pattern: \"I help <ICP> <outcome> without <the usual pain>. Building <product>.\"",
      tip: "Check the profile on mobile. Two lines of bio and the pinned post are all most people ever see.",
    },
    {
      id: "sequence",
      title: "Run the reply-first outreach sequence",
      summary:
        "The sequence that works on X is public before private. Reply, get recognised, then move to DMs. Reversing that order is what makes outreach feel like spam and get reported.",
      steps: [
        "Reply within the hour with a specific answer that helps even if they never buy.",
        "Mention your product only if it is the direct answer to their question, and say plainly that it is yours.",
        "Follow them; let them see your posts for a few days.",
        "DM only after they reply to you, and open with the context of that exchange.",
        "If there is no reply after two touches, stop. Add them to a List and wait for the next signal.",
      ],
      example: "Reply → follow → (their reply) → DM with context → one follow-up → stop.",
      tip: "Write replies that read well to a stranger. Most of the value comes from the audience, not the author.",
    },
    {
      id: "scoring",
      title: "Score, route and follow up",
      summary:
        "Volume is not the constraint on X — attention is. A simple score keeps your best hour on the best five posts instead of the first fifty you saw.",
      steps: [
        "Score intent 0–50: explicit request 50, active complaint 40, trigger event 25, topical question 10.",
        "Score fit 0–50: title, company size, industry, whether they can actually buy.",
        "Reply to anything over 70 the same day; everything else goes to a List for nurture.",
        "Log the account, the post URL and the date in your CRM — a lead with no record is a lead you lose.",
        "Review weekly: which queries produced replies, which produced conversations, which produced pipeline.",
      ],
      example: "Explicit request (50) + exact ICP (45) = 95 → reply within the hour.",
      tip: "Track replies-to-conversations, not just volume. The ratio tells you whether your queries or your copy need work.",
    },
  ],
  routine: {
    heading: "The 30-minute daily routine",
    intro:
      "Lead generation on X fails when it depends on inspiration. This is the loop that survives a busy week.",
    steps: [
      {
        name: "Run your saved searches (5 min)",
        text: "Open your five bookmarked intent queries on the Latest tab and skim the last 24 hours.",
      },
      {
        name: "Reply to the top posts (10 min)",
        text: "Pick the three to five highest-intent posts and write a specific, useful public reply to each.",
      },
      {
        name: "Check competitor complaints (5 min)",
        text: "Scan your competitor and to:competitor queries for anyone actively unhappy or shopping.",
      },
      {
        name: "Engage your ICP List (5 min)",
        text: "Comment on five posts from your List — substance, not likes. This is what makes the next reply land.",
      },
      {
        name: "Log and schedule follow-up (5 min)",
        text: "Record every lead with its post URL and score, and set the follow-up date for anyone who did not reply.",
      },
    ],
  },
  mistakes: [
    {
      title: "Pitching in the first reply",
      body: "The fastest way to get muted. Answer the question first; the product mention earns its place in the second exchange, if at all.",
    },
    {
      title: "Copy-pasting the same reply at scale",
      body: "Identical replies across many accounts are exactly what platform-manipulation rules target, and readers spot them instantly.",
    },
    {
      title: "Searching your category name",
      body: "\"CRM\" returns competitors, job ads and news. Buyers describe problems, not categories.",
    },
    {
      title: "Living on the Top tab",
      body: "Top ranks by engagement, which means old posts. Every answerable post is on Latest.",
    },
    {
      title: "Cold DMs before any public interaction",
      body: "Unsolicited pitch DMs get reported, and the report costs you the account you built the audience on.",
    },
    {
      title: "No system behind the replies",
      body: "Without a score, a log and a follow-up date, a good month on X leaves no pipeline behind it.",
    },
  ],
  faqs: [
    {
      q: "Is X (Twitter) still a good channel for B2B lead generation?",
      a: "Yes, for products with a technical, founder or marketing audience. The advantage is not audience size — it is that intent is public and timestamped, so you can reach someone in the hour they are actually deciding. If your buyers are not on X (many regulated and enterprise-only segments are not), LinkedIn is the better first channel.",
    },
    {
      q: "Do I need X Premium or API access to find leads?",
      a: "No. Every operator in this guide works in the free web search box. Premium adds X Pro's multi-column view, which makes monitoring several saved searches at once much faster, and API access matters only if you are building automated monitoring rather than doing it by hand.",
    },
    {
      q: "Is it against X's rules to DM people you find in search?",
      a: "A relevant one-to-one DM is fine. What breaks the rules is bulk and repetition: sending the same unsolicited message to many accounts, or DMing people who have never interacted with you, falls under X's spam and platform-manipulation policies. Reply publicly first, and DM only after the person has engaged with you.",
    },
    {
      q: "How many leads can I realistically find per week on X?",
      a: "For a typical B2B niche, five well-tuned queries surface roughly 10–40 genuinely high-intent posts a week. The number that matters more is reply rate: a specific, helpful public reply usually earns a response from a quarter to a third of the people you answer, which is far above cold email.",
    },
    {
      q: "Should I reply publicly or send a DM?",
      a: "Reply publicly, every time. The author sees it, everyone watching the thread sees it, and it stays searchable. A DM reaches one person and looks like a sales move. Use DMs only to continue a conversation that already started in the open.",
    },
    {
      q: "Can finding leads on X be automated?",
      a: "The finding can be — search, filtering, scoring and drafting are all mechanical. The sending should not be. Fully automated replies read as spam, damage the account, and violate the rules on inauthentic engagement. The workable pattern is automated discovery with human approval before anything is posted, which is exactly how Collavue is built.",
    },
  ],
  comparison: {
    heading: "Doing this by hand vs running it with Collavue",
    rows: [
      ["Finding intent posts", "5–10 manual searches a day", "Continuous monitoring across your queries"],
      ["Filtering noise", "You read everything", "Intent + ICP-fit score, 0–100"],
      ["Competitor complaints", "Whenever you remember to check", "Standing coverage, alerted"],
      ["Writing the reply", "Blank box, every time", "Draft in your brand voice, ready to edit"],
      ["Sending", "Manual", "Human approval — nothing posts on its own"],
      ["Tracking", "Bookmarks and memory", "Lead inbox with source post and outcome"],
    ],
  },
};

const LINKEDIN = {
  key: "linkedin",
  slug: "linkedin-lead-generation",
  path: "/linkedin-lead-generation",
  platform: "LinkedIn",
  shortName: "LinkedIn",
  navLabel: "Find leads on LinkedIn",
  seo: {
    title: "How to Find Leads on LinkedIn: 9 Methods That Work",
    description:
      "Find B2B leads on LinkedIn: Boolean searches on the Posts tab, Sales Navigator Spotlights, hiring signals, and a warm outreach sequence that gets replies.",
    keywords:
      "how to find leads on linkedin, linkedin lead generation, linkedin prospecting, sales navigator search, linkedin boolean search, find clients on linkedin, b2b lead generation linkedin, linkedin social selling, linkedin outreach strategy",
  },
  hero: {
    eyebrow: "Playbook · LinkedIn",
    h1: "How to find leads on LinkedIn",
    h1Italic: "that actually reply.",
    intro:
      "LinkedIn knows your buyer's title, company size, tenure and who they know — no other network lets you filter that precisely. The catch is that everyone is prospecting there, so precision and warmth are the whole game. This guide covers the searches that find real intent, the signals worth building a list around, and the outreach sequence that gets a reply instead of a dismissal.",
  },
  answer: {
    heading: "The short answer",
    body: "Find leads on LinkedIn by stacking three layers. First, search conversations: run Boolean queries like (\"looking for\" OR \"recommendations for\") AND (\"CRM\" OR \"sales software\") on the Posts tab, sorted by Latest, to catch people describing your problem right now. Second, build lists from firmographics and intent — Sales Navigator filters plus Spotlights such as changed jobs in the last 90 days. Third, mine engagement on the posts your buyers already read. Warm every lead with a relevant comment before the connection request, and keep invitations well inside LinkedIn's weekly limit.",
    bullets: [
      "The Posts tab is where intent lives; most reps only ever search People.",
      "Sales Navigator Spotlights (job change, recent posts, follows your company) are the best free-standing intent filter on the platform.",
      "Comment before you connect — recognition roughly doubles acceptance in practice.",
      "Send connection notes with context, not pitches; you have 300 characters.",
      "Stay manual or human-approved: automation tools violate LinkedIn's User Agreement.",
    ],
  },
  quickFacts: [
    {
      title: "Firmographics are built in",
      body: "Title, seniority, headcount, industry, geography and tenure are all filterable — you can define an ICP precisely instead of inferring it from a bio.",
    },
    {
      title: "Invitations are rate-limited",
      body: "LinkedIn caps how many invitations you can send (commonly cited at around 100–200 per week, and it varies by account). Precision beats volume by necessity.",
    },
    {
      title: "Automation is against the rules",
      body: "The User Agreement prohibits scrapers and unauthorised automation. Tools that auto-connect or auto-message put the account at risk of restriction.",
    },
    {
      title: "Warm paths are visible",
      body: "Mutual connections, shared groups, alumni and colleague relationships are all surfaced — and a warm intro still outperforms every cold sequence.",
    },
  ],
  signalsIntro:
    "LinkedIn gives you two kinds of signal: who someone is, and what just changed for them. The second is what makes outreach timely.",
  signals: [
    {
      signal: "Post asking for recommendations",
      example: "\"What are people using for contract management these days?\"",
      why: "Explicit intent, in public, with the comment thread showing you exactly who the competitors are.",
    },
    {
      signal: "New role or promotion",
      example: "\"Starting today as VP Sales at Acme.\"",
      why: "First 90 days is the change window: new mandate, new budget, and a reason to re-evaluate the stack.",
    },
    {
      signal: "Company hiring for an adjacent role",
      example: "A job post for a RevOps or data analyst.",
      why: "Job descriptions list the tools and the pain in plain text. Sometimes your product is the cheaper answer.",
    },
    {
      signal: "Funding, expansion or new market",
      example: "\"We've raised our Series A to expand into the US.\"",
      why: "Capital and headcount growth turn into tooling decisions within a quarter.",
    },
    {
      signal: "Commented on a competitor's post",
      example: "Someone asking a pointed question under a competitor's launch.",
      why: "They are actively evaluating that category, and they told you which vendor is already in the consideration set.",
    },
    {
      signal: "Attending a relevant event",
      example: "An industry webinar or a LinkedIn Event.",
      why: "Attendance is a public statement of interest in the topic, and the attendee feed is a legitimate conversation starter.",
    },
    {
      signal: "Viewed your profile",
      example: "Appears in \"Who's viewed your profile\".",
      why: "They came to you. This is the warmest inbound signal LinkedIn gives away for free.",
    },
    {
      signal: "Complaint about an incumbent tool",
      example: "\"Two weeks waiting on support and still nothing.\"",
      why: "Rarer than on X because LinkedIn is a professional stage — which makes it more meaningful when it happens.",
    },
  ],
  cheatsheet: {
    heading: "LinkedIn Boolean search strings that surface intent",
    intro:
      "Put these in the search bar, then open the Posts tab and sort by Latest to catch live conversations, or the People tab to build a list. Boolean is most reliable in Sales Navigator's keyword fields — LinkedIn has changed support in free search more than once, so sanity-check that your operators are actually narrowing the result count.",
    rows: [
      {
        query: `("looking for" OR "any recommendations") AND ("CRM" OR "sales tool")`,
        finds: "Live requests for a recommendation. Run it on Posts, sorted by Latest.",
      },
      {
        query: `("switching from" OR "moving off" OR "migrating away from") "Competitor"`,
        finds: "Displacement intent — the shortest sales cycle available on the platform.",
      },
      {
        query: `("we're hiring" OR "join our team") AND ("RevOps" OR "sales operations")`,
        finds: "Capacity and stack signals: hiring for a role adjacent to what you sell.",
      },
      {
        query: `("just joined" OR "excited to share that I") AND ("Head of" OR "VP of")`,
        finds: "New-in-role buyers inside their 90-day change window.",
      },
      {
        query: `"head of revenue operations" NOT recruiter NOT "talent"`,
        finds: "A clean People-tab list with recruiters and agency noise excluded.",
      },
      {
        query: `("frustrated with" OR "disappointed in") AND ("our current" OR "the tool we use")`,
        finds: "Softly worded complaints — the LinkedIn version of venting.",
      },
      {
        query: `("does anyone know" OR "asking for a friend") AND "compliance software"`,
        finds: "Question-shaped posts where a genuinely useful comment outperforms any DM.",
      },
      {
        query: `("we just closed" OR "thrilled to announce our") AND ("seed" OR "Series A")`,
        finds: "Funding triggers, filterable further by geography and headcount.",
      },
    ],
    notes: [
      "Quotes match exact phrases; parentheses group; AND, OR and NOT must be uppercase.",
      "Sort by Latest on the Posts tab — the default relevance sort buries anything still answerable.",
      "In Sales Navigator, save the search and turn on alerts so new matches come to you.",
      "Filter people out as well as in: NOT recruiter, NOT student and NOT intern clean most lists dramatically.",
    ],
  },
  methodsIntro:
    "Nine methods that work without automation tools. The first four find the people; the last five are what turn a list into conversations.",
  methods: [
    {
      id: "post-search",
      title: "Search conversations, not just people",
      summary:
        "Most prospecting starts on the People tab, which tells you who someone is but nothing about whether they need anything today. The Posts tab is where intent shows up, and it is dramatically less crowded.",
      steps: [
        "Write intent phrases the way professionals phrase them: \"looking for\", \"recommendations for\", \"what are people using\", \"has anyone tried\".",
        "Combine with your category terms using AND / OR and run the search.",
        "Open the Posts tab and change the sort to Latest.",
        "Filter by date posted (past 24 hours or past week) to keep results actionable.",
        "Comment on the post itself before you consider a connection request.",
      ],
      example: `("what are people using" OR "recommendations for") AND ("onboarding software" OR "client portal")`,
      tip: "Read the comments before you write one. Adding the point nobody made beats repeating the top answer.",
    },
    {
      id: "sales-navigator",
      title: "Build lists with Sales Navigator filters and Spotlights",
      summary:
        "Sales Navigator's value is not the extra search results, it is the Spotlights: changed jobs in the last 90 days, posted on LinkedIn in the past 30 days, follows your company, and shares experiences with you. Each one turns a static list into a timed one.",
      steps: [
        "Define the account list first: industry, headcount, geography, and growth or funding filters.",
        "Layer lead filters on top: function, seniority, title, and years in current position under one.",
        "Apply Spotlights — start with \"changed jobs in the past 90 days\" and \"posted on LinkedIn in the past 30 days\".",
        "Save the search and enable alerts so new matches arrive weekly.",
        "Work the list in priority order, not top to bottom.",
      ],
      example: "Lead filters: Head of / VP, Sales or RevOps, 50–500 employees, changed jobs in past 90 days.",
      tip: "\"Posted on LinkedIn in the past 30 days\" is the most underrated filter there is — active posters reply far more often than dormant profiles.",
    },
    {
      id: "engagement-mining",
      title: "Mine engagement on the posts your buyers already read",
      summary:
        "When a competitor launches something, or an industry voice posts about your category, the reaction list is a pre-qualified audience: they self-selected into the topic in the last 48 hours.",
      steps: [
        "Follow your competitors and the ten people your buyers actually read.",
        "When one posts something adjacent to your category, open the reactions and comments.",
        "Filter mentally for your ICP — title and company are right there in the list.",
        "Engage with their own recent post first, so your name is not brand new.",
        "Connect with a note that references the shared thread, not your product.",
      ],
      example: "Competitor launch post → 120 reactions → 15 match your ICP → 15 warm, contextual openers.",
      tip: "People who commented are a much stronger signal than people who reacted. Start there.",
    },
    {
      id: "hiring-signals",
      title: "Use hiring and job posts as intent data",
      summary:
        "A job description is the most honest document a company publishes. It names the stack, the gap, the team structure and the urgency — often things no sales call would surface.",
      steps: [
        "Search Jobs for roles adjacent to your product, filtered to your target company sizes.",
        "Read the responsibilities and required tools; note the competitors named.",
        "Identify the hiring manager — usually the person your product would report into.",
        "Reach out about the problem the role exists to solve, not about the role.",
        "Track repeat postings: a role re-posted twice means the pain is unresolved.",
      ],
      example: "\"Hiring an analyst to build weekly reporting\" → a reporting gap you may close for less than the salary.",
      tip: "Never open with \"I saw you're hiring\". Open with the problem the hire implies.",
    },
    {
      id: "events-groups",
      title: "Work LinkedIn Events and Groups",
      summary:
        "Events give you a public attendee list of people who set aside an hour for your topic, and the event feed is a legitimate place to talk to them. Groups are quieter than they were, but niche ones are still active.",
      steps: [
        "Find events in your category — including competitors' webinars — and register.",
        "Post one substantive comment or question in the event feed before it starts.",
        "During the session, note who asks good questions.",
        "Connect afterwards referencing the specific session and their specific point.",
        "Consider hosting your own event; the attendee list is the lead list.",
      ],
      example: "Attend three category events a quarter; connect with the ten most engaged attendees at each.",
      tip: "Attending a competitor's webinar is not sneaky — it is where their evaluating buyers are, in public.",
    },
    {
      id: "profile",
      title: "Turn your profile into a landing page",
      summary:
        "Everyone you comment on checks your profile. Treat the headline and About section as a landing page for one specific buyer instead of a résumé.",
      steps: [
        "Headline: who you help, the outcome, and how — not just your job title.",
        "About: open with the problem your buyer has, in their words, in the first two lines (the rest is behind \"see more\").",
        "Featured: pin the one asset that proves it — a case study, a demo, a teardown.",
        "Banner: state the offer plainly.",
        "Add the keywords your buyers search, because LinkedIn search reads these fields.",
      ],
      example: "Headline pattern: \"Helping <ICP> <outcome> | <how> | <proof>\"",
      tip: "Check how the first two lines of your About section render on mobile. That is what most visitors read.",
    },
    {
      id: "content",
      title: "Post so buyers find you",
      summary:
        "Outbound gets you this quarter's meetings; content makes next quarter's outbound work, because people recognise your name before you arrive. It also generates its own inbound in the comments.",
      steps: [
        "Pick three formats and rotate: the specific how-to, the contrarian take with evidence, and the customer story with numbers.",
        "Post two to three times a week, consistently, at a time your audience is online.",
        "Reply to every comment within the first hour.",
        "Treat commenters as leads: check the profile, and follow up with the ones who match.",
        "Repurpose the best-performing post into your outreach opener.",
      ],
      example: "One post about a specific problem → 40 comments → 6 ICP-matching commenters → 6 warm conversations.",
      tip: "The comments on your posts are a lead list most people never work. Check them like an inbox.",
    },
    {
      id: "sequence",
      title: "Run the warm outreach sequence",
      summary:
        "Connect-and-pitch is why LinkedIn inboxes are ignored. The sequence that works spends its first two touches earning recognition and gives the ask somewhere to land.",
      steps: [
        "Engage first: a substantive comment on a recent post, two or three days before you connect.",
        "Send the invitation with a note (300 characters) that references why you are connecting — no pitch.",
        "After acceptance, send something useful with no ask: a relevant teardown, benchmark or article.",
        "Only then make a soft, specific ask tied to something they said publicly.",
        "Two follow-ups maximum, spaced a week apart, then stop and keep them on the nurture list.",
      ],
      example: "Comment → connect with context → value message → soft ask → one follow-up → stop.",
      tip: "If you cannot name a specific reason you are messaging this person today, you are not ready to message them.",
    },
    {
      id: "tracking",
      title: "Track it like a pipeline, not an inbox",
      summary:
        "LinkedIn conversations are easy to lose because the platform is not a CRM. What separates teams that get pipeline from teams that get busy is a record and a review.",
      steps: [
        "Save searches and turn on alerts so new matches come to you instead of you re-running searches.",
        "Log every lead: profile URL, the signal that triggered outreach, the date, and the score.",
        "Score intent and ICP fit the same way you would any other channel, 0–100.",
        "Set a follow-up date at the moment you send, not later.",
        "Review weekly: which signals converted, which openers got replies, which lists went cold.",
      ],
      example: "New-in-role + exact ICP + posted this week = top of the queue.",
      tip: "Measure accepted-connection-to-conversation rate. It tells you whether your problem is targeting or your opener.",
    },
  ],
  routine: {
    heading: "The 30-minute daily routine",
    intro:
      "Thirty focused minutes beats an hour of scrolling. Run it at the same time every weekday.",
    steps: [
      {
        name: "Check alerts and saved searches (5 min)",
        text: "Review new matches from saved searches, job-change alerts and profile views.",
      },
      {
        name: "Search the Posts tab (5 min)",
        text: "Run two Boolean intent queries, sorted by Latest, filtered to the past 24 hours.",
      },
      {
        name: "Comment with substance (10 min)",
        text: "Leave five real comments on ICP posts — including anyone you plan to contact this week.",
      },
      {
        name: "Send connection requests (5 min)",
        text: "Send a small batch of invitations with context notes, well inside your weekly limit.",
      },
      {
        name: "Follow up and log (5 min)",
        text: "Work the follow-ups due today, then log every new lead with its signal and score.",
      },
    ],
  },
  mistakes: [
    {
      title: "Pitching in the connection note",
      body: "The single biggest cause of ignored requests. The note's only job is to explain why you, why them, why now.",
    },
    {
      title: "Connect-and-blast automation",
      body: "Auto-connect and auto-message tools violate LinkedIn's User Agreement and put the account at risk of restriction — and recipients recognise the templates anyway.",
    },
    {
      title: "Only searching People",
      body: "The People tab tells you who someone is. The Posts tab tells you what they need this week.",
    },
    {
      title: "\"Great post!\" comments",
      body: "Empty comments register as noise. One specific sentence that adds information is worth fifty of them.",
    },
    {
      title: "Ignoring the 90-day window",
      body: "New-in-role buyers are the most reachable people on the platform, and the window closes quietly.",
    },
    {
      title: "Treating InMail as the first move",
      body: "Paid messages sent cold convert poorly. Check mutual connections and recent activity first — a warm path usually exists.",
    },
  ],
  faqs: [
    {
      q: "Do I need Sales Navigator to find leads on LinkedIn?",
      a: "No. Boolean search on the Posts tab, engagement mining, job-post signals and events all work on a free account, and they cover most of the intent signals worth acting on. Sales Navigator raises the ceiling: saved searches with alerts, deeper filters and Spotlights like \"changed jobs in the past 90 days\" are hard to replicate manually at any scale.",
    },
    {
      q: "How many connection requests can I safely send per week?",
      a: "LinkedIn enforces a weekly invitation limit — widely reported at roughly 100–200 and varying by account age and behaviour. Practically, staying well under it and personalising every note produces better results than hitting the cap: acceptance rate matters far more than volume, and a low acceptance rate makes the account look spammy.",
    },
    {
      q: "Are LinkedIn automation tools allowed?",
      a: "No. LinkedIn's User Agreement prohibits scraping and unauthorised automated access, and accounts using auto-connect or auto-message tools risk restriction or permanent loss. Software can help you find and prioritise leads and draft messages — but a person should approve and send anything that leaves your account.",
    },
    {
      q: "What converts better, InMail or a connection request?",
      a: "A connection request with a context note usually outperforms cold InMail, because it costs nothing, it arrives with your profile attached, and acceptance itself is a small commitment that makes the next message welcome. Save InMail for people you cannot reach otherwise, and only after you have engaged with their content.",
    },
    {
      q: "How often should I post to generate inbound leads?",
      a: "Two or three times a week, sustained, beats a daily burst that stops after a month. Consistency is what makes your name familiar when your outreach lands. Reply to every comment in the first hour — the comment thread generates more conversations than the post itself.",
    },
    {
      q: "Does commenting actually generate leads?",
      a: "Yes, and it is the most reliably underrated tactic on the platform. A specific comment on an ICP post is seen by the author and by their audience, it costs no invitation quota, and it makes a later connection request feel like a continuation rather than a cold approach.",
    },
  ],
  comparison: {
    heading: "Doing this by hand vs running it with Collavue",
    rows: [
      ["Finding intent posts", "Manual Boolean searches, when you remember", "Continuous monitoring across your queries"],
      ["Qualifying", "Open every profile", "Intent + ICP-fit score, 0–100"],
      ["Trigger events", "Alerts you check occasionally", "Surfaced in one lead inbox"],
      ["Writing the message", "Blank box, every time", "Draft in your brand voice, ready to edit"],
      ["Sending", "Manual", "Human approval — nothing sends on its own"],
      ["Tracking", "Spreadsheet, or nothing", "Lead inbox with source post and outcome"],
    ],
  },
};


const REDDIT = {
  key: "reddit",
  slug: "reddit-lead-generation",
  path: "/reddit-lead-generation",
  platform: "Reddit",
  shortName: "Reddit",
  navLabel: "Find leads on Reddit",
  seo: {
    title: "How to Find Leads on Reddit: 9 Methods That Work",
    description:
      "Find B2B leads on Reddit without getting banned: search operators that surface buying intent, the subreddits worth monitoring, and a comment-first sequence that respects self-promotion rules.",
    keywords:
      "how to find leads on reddit, reddit lead generation, reddit marketing, find customers on reddit, reddit search operators, subreddit prospecting, reddit b2b leads, buying intent on reddit, reddit self promotion rules, reddit lead generation tool",
  },
  hero: {
    eyebrow: "Playbook · Reddit",
    h1: "How to find leads on Reddit",
    h1Italic: "without getting banned.",
    intro:
      "Reddit is where people ask the question they are too embarrassed to ask on LinkedIn: which tool actually works, what does it really cost, and is the one we are paying for worth keeping. Those threads are public, searchable, and they rank in Google for years. This is the playbook: the search operators, the subreddits worth monitoring, the rules that get marketers removed, and the comment-first sequence that gets upvoted instead of reported.",
  },
  answer: {
    heading: "The short answer",
    body: "To find leads on Reddit, monitor the subreddits where your buyers already ask for help and search them for buying intent rather than for your category. Run queries like subreddit:smallbusiness (\"recommend\" OR \"looking for\") \"invoicing\", sort by New, and answer inside the thread within the first few hours. Read each subreddit's self-promotion rule before you post, disclose that you work on the product any time you name it, and never lead with a link. A helpful comment on Reddit keeps producing leads for years because the thread ranks in Google long after the conversation ends.",
    bullets: [
      "Search intent phrases inside specific subreddits — subreddit: is the highest-leverage operator on the platform.",
      "Sort by New. The Top tab is a museum; Reddit threads are answerable for days, not minutes.",
      "Read the sidebar rules first. Most subreddits ban self-promotion outright, and mods enforce it.",
      "Disclose your affiliation in the same sentence you name your product. Undisclosed promotion is the fastest ban on Reddit.",
      "Comment value first, link last — often never. The profile is where interested people go looking.",
    ],
  },
  quickFacts: [
    {
      title: "The threads rank in Google",
      body: "A comment on Reddit is indexed and keeps arriving in search results for years. Reddit results now sit at the top of the page for most \"best tool for X\" queries, which makes one good answer a compounding asset rather than a one-day post.",
    },
    {
      title: "The window is days, not minutes",
      body: "Unlike X, a Reddit thread stays answerable for a day or two and keeps collecting readers for months. You do not need to be first — you need to be the most useful comment in the thread.",
    },
    {
      title: "The rules are enforced by humans",
      body: "Each subreddit sets its own self-promotion policy and moderators remove violations by hand. Account age and karma minimums are common, and a removal streak can get the account site-banned.",
    },
    {
      title: "Anonymity makes people honest",
      body: "People say what they actually pay, what actually broke, and why they actually churned. No other network gives you objections in the buyer's own words at this resolution.",
    },
  ],
  signalsIntro:
    "Not every mention is a lead. These are the eight post patterns worth acting on, roughly in order of how quickly they convert.",
  signals: [
    {
      signal: "Direct request for a recommendation",
      example: "\"What are you all using for client invoicing? Under $50/mo ideally.\"",
      why: "Highest intent on the platform, and the reply thread is where the decision actually gets made — often by upvote.",
    },
    {
      signal: "\"X vs Y\" comparison thread",
      example: "\"Anyone moved from Tool A to Tool B? Worth it?\"",
      why: "The poster has a shortlist and a budget. A specific, honest comparison — including where you lose — usually decides it.",
    },
    {
      signal: "Complaint about an incumbent",
      example: "\"Our vendor raised prices 40% at renewal with two weeks' notice.\"",
      why: "Budget already exists and the renewal date is a deadline. These are the highest-converting threads for most B2B tools.",
    },
    {
      signal: "\"Is it worth it?\" pricing question",
      example: "\"Is anyone actually getting value out of paying for this?\"",
      why: "The person is mid-evaluation and looking for permission to spend or to cancel. Either way they are in market now.",
    },
    {
      signal: "A workflow question you can answer",
      example: "\"How do you all handle chasing late invoices without annoying clients?\"",
      why: "Lower stated intent, but the thread ranks and every future searcher with that problem reads your answer.",
    },
    {
      signal: "Weekly or pinned recurring thread",
      example: "r/SaaS \"Self-promotion Saturday\", r/startups \"Share your startup\".",
      why: "The one place in the subreddit where naming your product is explicitly allowed. Low ceiling, zero risk.",
    },
    {
      signal: "Build-versus-buy debate",
      example: "\"Should we just build this internally with a couple of scripts?\"",
      why: "The buyer is pricing options in public. Real numbers about maintenance cost usually end the debate.",
    },
    {
      signal: "Launch, funding or hiring post",
      example: "\"We just hit 20 people and our processes are falling apart.\"",
      why: "Growth pain is a trigger event. The problem is stated before the category is, which means no competitor is in the thread yet.",
    },
  ],
  cheatsheet: {
    heading: "Reddit search operators for lead generation",
    intro:
      "Paste these into the Reddit search bar, then sort by New and set the time filter to the last week. Swap the quoted phrases for your own category and competitor names. Operators combine with AND, OR and NOT, and quotes match exact phrases. Everything below also works on old.reddit.com, which paginates far better for scanning.",
    rows: [
      {
        query: `subreddit:smallbusiness ("recommend" OR "recommendations for") "invoicing"`,
        finds:
          "Recommendation requests inside one community, which is the single highest signal-to-noise query on Reddit.",
      },
      {
        query: `("looking for a" OR "any suggestions for") "project management" self:yes nsfw:no`,
        finds:
          "Text posts (self:yes) where someone described a need in their own words, rather than link drops.",
      },
      {
        query: `title:"vs" competitorname`,
        finds:
          "Comparison threads with your competitor in the title — buyers with a shortlist and a decision to make.",
      },
      {
        query: `"alternative to" competitorname OR "switching from" competitorname`,
        finds:
          "Displacement intent. Budget exists, the incumbent has already failed, and the thread ranks in Google afterwards.",
      },
      {
        query: `selftext:"we're paying" AND selftext:"too expensive"`,
        finds:
          "Price-driven churn risk stated inside the post body, where the real numbers usually live.",
      },
      {
        query: `subreddit:sysadmin ("how do you all" OR "how does everyone") "onboarding"`,
        finds:
          "Workflow questions you can answer with authority — the comments that keep earning traffic for years.",
      },
      {
        query: `flair:"Question" "recommendations"`,
        finds:
          "Subreddits that use post flair make it trivial to isolate the question posts from the news and memes.",
      },
      {
        query: `author:username`,
        finds:
          "Everything one promising poster has written — the fastest way to qualify a lead before you reply to them.",
      },
      {
        query: `site:reddit.com/r/marketing "anyone recommend" "email tool"`,
        finds:
          "The same search run in Google, which indexes Reddit comments — not just posts — and often surfaces threads Reddit's own search misses.",
      },
      {
        query: `https://www.reddit.com/r/SaaS/new/.rss`,
        finds:
          "Any subreddit, search or user feed with .rss appended becomes a feed you can pipe into a reader or a monitor. No API key needed.",
      },
    ],
    notes: [
      "Sort by New and set the time filter explicitly — Reddit's default Relevance sort buries this week's posts under three-year-old threads.",
      "subreddit:, author:, title:, selftext:, flair:, self: and nsfw: are the operators that actually work; most other syntax you will find in old blog posts was removed.",
      "Reddit's own search misses comments. Run the important queries through Google with site:reddit.com as well.",
      "Add .rss to a search URL to turn any working query into a standing monitor without touching the API.",
      "Save each working query as a browser bookmark. A query you have to retype is a query you stop running.",
    ],
  },
  methodsIntro:
    "Nine methods, ordered from the fastest to set up to the most compounding. On Reddit the first two are non-negotiable — skipping the rules research is what gets accounts banned in week one.",
  methods: [
    {
      id: "map-subreddits",
      title: "Map the subreddits your buyers actually post in",
      summary:
        "Reddit is not one audience, it is ten thousand. The whole game is picking the six to ten communities where your buyer already asks for help, and ignoring the rest — including the big general subreddits where your post will be removed on sight.",
      steps: [
        "Search your category and read which subreddits the results come from, not just the posts.",
        "Check the sidebar of each: subscriber count, posting rules, and whether questions get real answers or crickets.",
        "Look at related-subreddit lists in the sidebar and wiki — that is where the smaller, higher-intent communities hide.",
        "Prefer a 40k-member niche subreddit with daily questions over a 3M-member subreddit that removes everything.",
        "Write the list down with each one's self-promotion rule next to it. That list is your territory.",
      ],
      example:
        "A payroll tool for agencies: r/agency, r/smallbusiness, r/Accounting, r/bookkeeping, r/Entrepreneur — not r/business.",
      tip: "If a subreddit's last ten questions have zero real answers, it is a dead room. Cross it off and spend the time elsewhere.",
    },
    {
      id: "read-the-rules",
      title: "Read every rule before you write a single comment",
      summary:
        "Every subreddit has its own self-promotion policy and its own moderators enforcing it by hand. On Reddit this is not fine print — it is the difference between a channel that compounds and an account that is gone in a fortnight.",
      steps: [
        "Open the sidebar and the wiki rules page for each subreddit on your list.",
        "Note the three that matter: can you name a product, can you link to it, and is there a promo thread instead.",
        "Check for account age and karma minimums — many subreddits silently remove posts from new accounts.",
        "Use one real, named account. Multiple accounts pushing the same product is vote manipulation and gets site-wide bans.",
        "When in doubt, message the moderators and ask. Mods who are asked first are dramatically more forgiving later.",
      ],
      example:
        "Common rule shape: \"No self-promotion outside the weekly thread. Answering a direct question about your product is fine if you disclose.\"",
      tip: "Reddit's own site-wide guidance is the 9:1 rule — nine contributions for every one about yourself. Treat it as the floor, not the target.",
    },
    {
      id: "intent-search",
      title: "Search buying intent inside those subreddits",
      summary:
        "The mistake almost everyone makes is searching their category name across all of Reddit. That returns news, memes and competitors. Scoping an intent phrase to one subreddit is what turns Reddit search from useless into the best free lead source you have.",
      steps: [
        "List the five ways a customer describes the problem before they know your category exists.",
        "Wrap each in an intent phrase: \"recommend\", \"looking for\", \"any suggestions\", \"what do you use for\", \"is there something that\".",
        "Scope it with subreddit: and add self:yes to drop link spam.",
        "Sort by New and set the time filter to the last week.",
        "Bookmark each working query, or append .rss and pipe it into your reader.",
      ],
      example: `subreddit:Entrepreneur ("what do you use for" OR "any suggestions for") "bookkeeping" self:yes`,
      tip: "Run the same query in Google with site:reddit.com. Reddit's search does not index comments, and half the intent lives there.",
    },
    {
      id: "competitor-mining",
      title: "Mine competitor threads and churn complaints",
      summary:
        "Every competitor has a public archive of unhappy customers on Reddit: price-increase threads, outage threads, \"is anyone else seeing this\" threads. The budget already exists, so the only question is whether you show up useful or spammy.",
      steps: [
        "Build a list of every competitor's product name and the common shorthands people actually type.",
        "Run \"alternative to\", \"switching from\", \"moving off\" and \"cancelling\" against each name.",
        "Set standing searches on title:\"vs\" plus each competitor for comparison threads.",
        "Answer the underlying question honestly, including where your product is the worse choice.",
        "Disclose your affiliation in the same sentence you name your product — every time, without exception.",
      ],
      example: `("alternative to" OR "switching from" OR "moving off") competitorname sort:new`,
      tip: "Do not pile onto a competitor's outage thread. Reddit is unusually good at spotting a rival's marketing team and unusually harsh about it.",
    },
    {
      id: "comment-first",
      title: "Answer in comments before you ever post",
      summary:
        "Posts get scrutinised; comments get read. On Reddit the comment is the unit of trust, and a track record of useful comments is what makes a later mention of your product land as a recommendation instead of an ad.",
      steps: [
        "Answer three questions a day in your subreddits with no mention of your product at all.",
        "Write the answer that would help even if your company did not exist — including naming competitors when they fit better.",
        "Mention your product only when it is the direct answer to the question asked, and say plainly that you built it.",
        "Never open with a link. If someone wants it, they will ask or check your profile.",
        "Track which comments earned replies. That is your signal for which threads to keep watching.",
      ],
      example:
        "\"Full disclosure, I build one of these (X), so take it with salt — but for your case Y is honestly the better fit because…\"",
      tip: "The comment that names a competitor as the better answer is the one that earns you the next five leads. Reddit rewards it.",
    },
    {
      id: "promo-threads",
      title: "Use the promo threads and AMAs the rules do allow",
      summary:
        "Most subreddits that ban self-promotion run a sanctioned alternative: a weekly promo thread, a \"share your startup\" post, a feedback thread, or an occasional AMA. Low ceiling, but zero moderation risk and a steady trickle of qualified replies.",
      steps: [
        "Find the recurring threads in each subreddit and put them in your calendar on the day they post.",
        "Write for the reader, not the algorithm: the problem you solve, who it is not for, and a real price.",
        "Answer every reply in that thread the same day — the conversation is where the leads are, not the post.",
        "Offer feedback to other posters in the same thread. It is the cheapest goodwill on the platform.",
        "Pitch an AMA to the mods only once you have a visible history of helpful comments in that subreddit.",
      ],
      example:
        "r/SaaS \"Self-promotion Saturday\", r/startups \"Share Your Startup\", r/smallbusiness weekly promo thread.",
      tip: "Post early in the thread's life. These threads are sorted by new for the first hours and then nobody scrolls.",
    },
    {
      id: "profile",
      title: "Turn your profile into the landing page",
      summary:
        "Every good comment sends a handful of curious people to your profile. Because you are not allowed to link in most threads, the profile is doing the work the link would do — and unlike a comment, nobody can remove it.",
      steps: [
        "Write a profile bio that says who you help and what you built, in one line.",
        "Pin a post that demonstrates the product solving a real problem, ideally one you posted in an allowed thread.",
        "Put the link in the profile's website field, pointed at a specific page rather than the homepage.",
        "Keep your recent comment history genuinely useful — it is the first thing anyone checks.",
        "Use one consistent account. Reddit trust is earned by the username, not the brand.",
      ],
      example:
        "Bio pattern: \"I help <ICP> <outcome>. Built <product>. Happy to answer <topic> questions here.\"",
      tip: "Check your own profile as a logged-out visitor. Removed comments and downvoted replies are more visible than you think.",
    },
    {
      id: "monitoring",
      title: "Set up standing monitoring instead of manual checking",
      summary:
        "Reddit's answerable window is days, not minutes, but only if you actually see the thread. Manual checking dies in a busy week — a standing monitor is what turns this from a burst into a channel.",
      steps: [
        "Append .rss to your best search and subreddit URLs and load them into a reader.",
        "Set Reddit's own keyword alerts and email digests for your product and competitor names.",
        "Check the monitor once in the morning and once at the end of the day; twice is enough on Reddit.",
        "Keep a short list of thread URLs you are waiting on replies in, and clear it daily.",
        "Review weekly: which subreddits produced conversations, and which produced only noise.",
      ],
      example:
        "https://www.reddit.com/search.rss?q=subreddit%3ASaaS+%22recommend%22&sort=new",
      tip: "Monitoring beats posting volume. The teams that win on Reddit read far more than they write.",
    },
    {
      id: "scoring",
      title: "Score, route and follow up",
      summary:
        "Reddit's constraint is not volume, it is patience — you are allowed one good answer per thread and no follow-up spam. A simple score keeps that one answer going to the threads that can actually turn into pipeline.",
      steps: [
        "Score intent 0–50: explicit recommendation request 50, comparison thread 40, active complaint 40, workflow question 15.",
        "Score fit 0–50: does the poster's described company, budget and role match who you sell to.",
        "Answer anything over 70 the same day; leave the rest to the monitor.",
        "Log the thread URL, the subreddit and the date — Reddit conversations resurface months later from search.",
        "Follow up only when the person replies. An unsolicited DM after one comment is how Reddit users report you.",
      ],
      example: "Explicit request (50) + exact ICP with stated budget (45) = 95 → answer today, in the thread.",
      tip: "Track comments-to-conversations. On Reddit a low ratio almost always means the comment was too promotional, not too short.",
    },
  ],
  routine: {
    heading: "The 30-minute daily routine",
    intro:
      "Reddit punishes bursts and rewards regulars. This is the loop that keeps an account in good standing while still producing leads.",
    steps: [
      {
        name: "Check your monitor (5 min)",
        text: "Open your saved searches and subreddit feeds, sorted by New, and skim everything posted since yesterday.",
      },
      {
        name: "Answer two or three questions (10 min)",
        text: "Write real answers to the highest-intent threads. No links, no product mention unless it is the direct answer — and disclose when it is.",
      },
      {
        name: "Contribute somewhere unrelated (5 min)",
        text: "Add one useful comment that has nothing to do with your product. This is what keeps the ratio — and the account — healthy.",
      },
      {
        name: "Check competitor searches (5 min)",
        text: "Scan your \"alternative to\" and comparison-thread queries for anyone actively shopping or churning.",
      },
      {
        name: "Log and revisit (5 min)",
        text: "Record every lead with its thread URL and score, and revisit yesterday's threads for replies you owe.",
      },
    ],
  },
  mistakes: [
    {
      title: "Dropping a link in your first comment",
      body: "The fastest removal on Reddit. Answer the question in the comment itself; if the answer needs a link, say so and let them ask for it.",
    },
    {
      title: "Not disclosing that you built the thing",
      body: "Undisclosed promotion is the one offence Reddit users escalate to moderators, and mods ban for it permanently. Disclose in the same sentence you name the product.",
    },
    {
      title: "Posting the same message across subreddits",
      body: "Cross-posted identical promotion is spam by Reddit's own definition, and the removal is site-wide rather than per-community.",
    },
    {
      title: "Using multiple accounts or asking for upvotes",
      body: "Vote manipulation is one of the few things that gets an account and its IP banned outright. One real, named account, always.",
    },
    {
      title: "Searching all of Reddit instead of your subreddits",
      body: "Unscoped search returns news, memes and competitors. subreddit: is what makes the results usable.",
    },
    {
      title: "Treating it like X and giving up after a week",
      body: "Reddit compounds through search: the comment you leave today keeps arriving in Google results for years. The channel rewards regulars, not sprinters.",
    },
  ],
  faqs: [
    {
      q: "Is Reddit good for B2B lead generation?",
      a: "Yes, for products whose buyers research in public — software, agencies, tools and services with a comparison-heavy purchase. Reddit's advantage is that people state budgets, objections and churn reasons anonymously, and the threads rank in Google for years, so one good answer keeps producing leads. The cost of entry is that self-promotion is strictly policed, so it only works if you show up as a participant.",
    },
    {
      q: "Will I get banned for promoting my product on Reddit?",
      a: "You get banned for promoting badly, not for having a product. The rules that matter are simple: follow each subreddit's self-promotion rule, disclose your affiliation whenever you name your own product, never use multiple accounts or ask for upvotes, and do not post the same promotional message across communities. Answering a direct question honestly — with disclosure — is welcome in most subreddits and is the whole method here.",
    },
    {
      q: "How do I find the right subreddits for my product?",
      a: "Search your category and look at which subreddits the good results come from, then read the sidebar and related-subreddit lists of each to find the smaller ones. Judge a community on whether real questions get real answers, not on subscriber count — a 40k-member niche subreddit with daily buying questions is worth more than a 3M-member general one that removes every commercial post.",
    },
    {
      q: "Does Reddit search actually work?",
      a: "It works well when you scope it. subreddit:, title:, selftext:, author:, flair:, self: and nsfw: all work, and combining a subreddit with an intent phrase is the highest-yield query on the platform. Its blind spot is comments — Reddit's own search does not index them, so run your important queries through Google with site:reddit.com as well, where the comment text is indexed.",
    },
    {
      q: "Should I DM people I find on Reddit?",
      a: "Rarely, and never as the first move. Reddit users treat unsolicited sales DMs as spam and report them, which costs you the account. Answer in the thread, let the person reply, and move to DMs only if they ask or if the conversation clearly needs to continue privately. Your profile does the rest of the work.",
    },
    {
      q: "Can Reddit lead generation be automated?",
      a: "The finding can be — searching, filtering and scoring are mechanical, and Reddit exposes .rss feeds and an API for exactly that. The commenting should not be. Automated posting violates Reddit's content policy on spam and inauthentic behaviour, and readers spot it instantly. The workable pattern is automated discovery with a human writing and approving every comment, which is how Collavue is built.",
    },
  ],
  comparison: {
    heading: "Doing this by hand vs running it with Collavue",
    rows: [
      ["Finding intent threads", "Manual subreddit searches, when you remember", "Continuous monitoring across your queries and subreddits"],
      ["Filtering noise", "You read every new post", "Intent + ICP-fit score, 0–100"],
      ["Competitor churn threads", "Whenever you think to check", "Standing coverage, alerted"],
      ["Writing the comment", "Blank box, every time", "Draft in your brand voice, ready to edit"],
      ["Posting", "Manual", "Human approval — nothing posts on its own"],
      ["Tracking", "Saved posts and memory", "Lead inbox with the thread URL and outcome"],
    ],
  },
};

export const LEAD_GUIDES = [TWITTER, LINKEDIN, REDDIT];

export function getLeadGuide(key) {
  return LEAD_GUIDES.find((g) => g.key === key || g.slug === key);
}

/** Channel comparison rendered on the hub page. */
export const CHANNEL_COMPARISON = {
  headers: ["", "X (Twitter)", "LinkedIn", "Reddit"],
  rows: [
    ["Who you reach", "Founders, developers, marketers, operators", "Every business function, including enterprise", "Practitioners and buyers researching in private"],
    ["Targeting precision", "Inferred from bio and behaviour", "Title, seniority, headcount, tenure — filterable", "By community, not by person — the subreddit is the filter"],
    ["Intent visibility", "Very high — complaints and requests are public", "Moderate — professional tone softens complaints", "Highest — anonymity means real budgets and real churn reasons"],
    ["Speed of the window", "Minutes to hours", "Days", "Days, then years in Google"],
    ["Search depth", "Powerful free operators", "Boolean plus Sales Navigator filters", "subreddit:, title:, selftext: — plus Google for comments"],
    ["Outreach limits", "No hard cap; spam rules apply", "Weekly invitation cap, enforced", "Per-subreddit self-promotion rules, enforced by mods"],
    ["Best first move", "A public reply", "A specific comment", "A useful comment with no link in it"],
    ["Best for", "Fast feedback, SMB and product-led motions", "Considered purchases and mid-market to enterprise", "Comparison-heavy purchases and compounding search traffic"],
  ],
};

export const HUB_FAQS = [
  {
    q: "Which is better for lead generation, X (Twitter), LinkedIn or Reddit?",
    a: "They solve different problems. X is fastest and most public: people state problems in plain language and you can answer within the hour, which suits SMB, product-led and technical audiences. LinkedIn is the most precise: you can filter by title, company size and tenure, and reach functions that never post elsewhere, which suits considered mid-market and enterprise purchases. Reddit is the most honest: anonymity means people post real budgets, real objections and real churn reasons, and the threads keep ranking in Google for years — which suits comparison-heavy purchases. Most teams that do this well run all three: X for speed, LinkedIn for precision, Reddit for depth and compounding search traffic.",
  },
  {
    q: "Is Reddit safe to use for lead generation?",
    a: "Yes, provided you follow each subreddit's self-promotion rule and disclose your affiliation whenever you name your own product. Reddit bans undisclosed promotion, duplicate cross-posting and multiple accounts — not participation. Teams that answer questions honestly, including recommending competitors when they fit better, do well there; teams that treat it as a posting channel get removed in the first fortnight.",
  },
  {
    q: "How long does social lead generation take to produce pipeline?",
    a: "Replies come in the first week; conversations in the first two. Pipeline usually follows in the first month for products with a short evaluation cycle, and a quarter for anything requiring procurement. The compounding part — people recognising your name before you contact them — takes about 90 days of consistency.",
  },
  {
    q: "Is social selling better than cold email?",
    a: "It works differently. Cold email reaches anyone, but starts from zero context. Social lead generation reaches fewer people and starts from something they said publicly, which is why reply rates are typically several times higher. The strongest pattern is to use social signals to decide who is worth emailing, and to warm the contact before the email arrives.",
  },
  {
    q: "Can this be automated safely?",
    a: "Discovery, scoring and drafting can be automated. Sending should stay human. Fully automated replies, comments and DMs violate all three platforms' rules on inauthentic engagement and automated access, and they read as spam to the people you are trying to win. Automate the search, keep a person on the send button.",
  },
];
